import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Wrench, Check } from "lucide-react";
import { SMART_HOME_KITS } from "../../data/valuePacks";
import KitCard from "../kits/KitCard";

/**
 * ValuePacksTeaser — homepage section, placed right after Hero.
 * Goal: show that a smart home starts at ₹39,999, not a ₹5–20 lakh project —
 * three standardised Smart Home Kits, no custom consultation required.
 */
export default function ValuePacksTeaser() {
  return (
    <section
      data-testid="smart-home-kits-teaser"
      className="relative bg-[var(--gx-bg)] py-20 lg:py-28 border-t border-line/60 grain overflow-hidden"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-gold/[0.04] blur-3xl" />

      <div className="relative max-w-[1400px] mx-auto px-5 lg:px-10">
        {/* Header */}
        <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-10 lg:gap-16 items-end mb-12 lg:mb-16">
          <div>
            <p className="eyebrow mb-5">Smart Home Kits · From ₹39,999</p>
            <h2 className="h1-display text-white">
              Start your smart home,<br />
              <span className="text-gold">simply.</span>
            </h2>
          </div>
          <div className="lg:pb-3">
            <p className="lead max-w-[52ch]">
              Three ready-made kits — no custom consultation needed. Choose what you need
              today, and add more whenever you're ready.{" "}
              <span className="text-white">Installed and supported end-to-end by GenieX.</span>
            </p>
          </div>
        </div>

        {/* Certified hardware trust strip */}
        <CertifiedHardwareBanner />

        {/* Kit cards */}
        <div className="grid md:grid-cols-3 gap-5 lg:gap-7 mt-10 lg:mt-14">
          {SMART_HOME_KITS.map((kit) => (
            <KitCard key={kit.key} kit={kit} compact />
          ))}
        </div>

        {/* Footer row */}
        <div className="mt-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="flex flex-wrap items-center gap-x-7 gap-y-3 text-[12.5px] text-dim">
            <span className="inline-flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-gold" /> 2-year warranty</span>
            <span className="inline-flex items-center gap-2"><Wrench className="w-4 h-4 text-gold" /> Professional installation</span>
            <span className="inline-flex items-center gap-2"><Check className="w-4 h-4 text-gold" /> No-cost EMI available</span>
          </div>
          <Link
            to="/value-packs"
            data-testid="smart-home-kits-explore-cta"
            className="btn-outline inline-flex items-center gap-2 group"
          >
            Explore Smart Home Kits
            <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function CertifiedHardwareBanner() {
  return (
    <div
      data-testid="hardware-trust-banner"
      className="relative rounded-[18px] border border-line bg-[var(--gx-surface)]/40 overflow-hidden"
    >
      <div className="grid sm:grid-cols-[auto_1fr_auto] items-center gap-5 p-4 sm:p-5">
        <div className="flex items-center justify-center">
          <div
            data-testid="hardware-badge"
            className="w-14 h-14 rounded-xl border border-gold/40 bg-white/[0.03] flex items-center justify-center"
          >
            <ShieldCheck className="w-6 h-6 text-gold" />
          </div>
        </div>

        <div className="text-center sm:text-left">
          <p className="text-[10px] tracking-[0.32em] uppercase text-mute">Hardware Engineered By</p>
          <p className="font-display text-lg sm:text-xl text-white mt-1 tracking-tight">
            GenieX Certified Hardware <span className="text-gold">·</span>{" "}
            <span className="text-dim text-[14px] font-normal">Tested for Indian voltage &amp; climate</span>
          </p>
        </div>

        <div className="hidden sm:flex items-center gap-2 text-[11px] text-amber-200/80 tracking-[0.22em] uppercase">
          <ShieldCheck className="w-4 h-4" /> Trust assured
        </div>
      </div>
    </div>
  );
}
